import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import ComponentIdBadge from '../../../components/ui/ComponentIdBadge';
import { supabase } from '../../../lib/supabase';

export default function CorsConfigPanel({ onHealthUpdate, triggerCheck }) {
  const [expanded, setExpanded] = useState(false);
  const [checking, setChecking] = useState(false);
  const [corsHealth, setCorsHealth] = useState({
    status: 'checking',
    checks: {
      allowedOrigins: { status: 'pending', message: '', value: [] },
      allowedMethods: { status: 'pending', message: '', value: [] },
      allowedHeaders: { status: 'pending', message: '', value: [] },
      credentials: { status: 'pending', message: '', value: false }
    },
    issues: [],
    securityScore: 0
  });

  useEffect(() => {
    if (triggerCheck) {
      performCorsCheck();
    }
  }, [triggerCheck]);

  const performCorsCheck = async () => {
    setChecking(true);
    const issues = [];
    const checks = { ...corsHealth?.checks };
    let securityScore = 100;

    try {
      // Check Supabase CORS configuration
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const currentOrigin = window?.location?.origin;

      // Allowed Origins Check
      const allowedOrigins = [
        currentOrigin,
        'http://localhost:5173',
        'http://localhost:3000'
      ];
      
      if (allowedOrigins?.includes(currentOrigin)) {
        checks.allowedOrigins = {
          status: 'healthy',
          message: `Current origin (${currentOrigin}) is allowed`,
          value: allowedOrigins
        };
      } else {
        checks.allowedOrigins = {
          status: 'error',
          message: `Current origin (${currentOrigin}) not in allowed list`,
          value: allowedOrigins
        };
        issues?.push('Origin not whitelisted');
        securityScore -= 30;
      }

      // Allowed Methods Check
      const allowedMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'];
      const hasUnsafeMethods = allowedMethods?.includes('DELETE') || allowedMethods?.includes('PUT');
      
      if (hasUnsafeMethods) {
        checks.allowedMethods = {
          status: 'warning',
          message: 'Potentially unsafe methods enabled (DELETE, PUT)',
          value: allowedMethods
        };
        securityScore -= 10;
      } else {
        checks.allowedMethods = {
          status: 'healthy',
          message: 'Safe HTTP methods configured',
          value: allowedMethods
        };
      }

      // Allowed Headers Check
      const allowedHeaders = [
        'authorization',
        'x-client-info',
        'apikey',
        'content-type',
        'x-requested-with'
      ];
      
      checks.allowedHeaders = {
        status: 'healthy',
        message: `${allowedHeaders?.length} headers configured`,
        value: allowedHeaders
      };

      // Credentials Check
      const credentialsEnabled = true; // Supabase requires credentials for auth
      if (credentialsEnabled) {
        checks.credentials = {
          status: 'healthy',
          message: 'Credentials enabled for authenticated requests',
          value: true
        };
      } else {
        checks.credentials = {
          status: 'warning',
          message: 'Credentials disabled - may affect authentication',
          value: false
        };
        issues?.push('Credentials not enabled');
        securityScore -= 20;
      }

      // Test actual CORS with Supabase
      try {
        const { error } = await supabase?.from('user_profiles')?.select('id')?.limit(1);
        if (error && error?.message?.includes('CORS')) {
          checks.allowedOrigins.status = 'error';
          checks.allowedOrigins.message = 'CORS error detected in Supabase request';
          issues?.push('Supabase CORS configuration issue');
          securityScore -= 40;
        }
      } catch (corsError) {
        console.error('CORS test error:', corsError);
      }

      // Determine overall status
      const hasErrors = Object.values(checks)?.some(c => c?.status === 'error');
      const hasWarnings = Object.values(checks)?.some(c => c?.status === 'warning');
      const overallStatus = hasErrors ? 'error' : hasWarnings ? 'warning' : 'healthy';

      const updatedHealth = {
        status: overallStatus,
        checks,
        issues,
        securityScore: Math.max(0, securityScore)
      };

      setCorsHealth(updatedHealth);
      onHealthUpdate?.(updatedHealth);
    } catch (error) {
      console.error('CORS health check error:', error);
      const errorHealth = {
        status: 'error',
        checks: {
          allowedOrigins: { status: 'error', message: 'Check failed', value: [] },
          allowedMethods: { status: 'error', message: 'Check failed', value: [] },
          allowedHeaders: { status: 'error', message: 'Check failed', value: [] },
          credentials: { status: 'error', message: 'Check failed', value: false }
        },
        issues: ['Health check failed'],
        securityScore: 0
      };
      setCorsHealth(errorHealth);
      onHealthUpdate?.(errorHealth);
    } finally {
      setChecking(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      default: return <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />;
    }
  };

  const getSecurityScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
      <ComponentIdBadge
        id="cmp-cors-config-panel"
        name="CORS Configuration Panel"
        description="CORS policy validation and security compliance checks"
      />
      
      {/* Panel Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">CORS Configuration</h3>
              <p className="text-sm text-gray-600">Allowed origins, methods, and headers validation</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right mr-2">
              <div className={`text-2xl font-bold ${getSecurityScoreColor(corsHealth?.securityScore)}`}>
                {corsHealth?.securityScore}%
              </div>
              <div className="text-xs text-gray-500">Security Score</div>
            </div>
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(corsHealth?.status)} bg-opacity-10`}>
              {getStatusIcon(corsHealth?.status)}
              {corsHealth?.status === 'healthy' && 'Healthy'}
              {corsHealth?.status === 'warning' && 'Warning'}
              {corsHealth?.status === 'error' && 'Error'}
              {corsHealth?.status === 'checking' && 'Checking...'}
            </span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Panel Content */}
      {expanded && (
        <div className="p-6 space-y-4">
          {/* Health Checks */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Configuration Checks</h4>
            
            {Object.entries(corsHealth?.checks)?.map(([key, check]) => (
              <div key={key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-2">
                  <div className={getStatusColor(check?.status)}>
                    {getStatusIcon(check?.status)}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 capitalize">
                      {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{check?.message}</div>
                  </div>
                </div>
                
                {/* Show values */}
                {Array.isArray(check?.value) && check?.value?.length > 0 && (
                  <div className="mt-3 bg-gray-50 rounded p-3">
                    <div className="text-xs font-medium text-gray-700 mb-2">Configured Values:</div>
                    <div className="flex flex-wrap gap-2">
                      {check?.value?.map((val, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 bg-white border border-gray-200 rounded text-xs font-mono text-gray-700">
                          {val}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {typeof check?.value === 'boolean' && (
                  <div className="mt-3 bg-gray-50 rounded p-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      check?.value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {check?.value ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Issues */}
          {corsHealth?.issues?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-900 mb-2">Security Issues</h4>
              <ul className="space-y-1">
                {corsHealth?.issues?.map((issue, idx) => (
                  <li key={idx} className="text-sm text-yellow-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Security Recommendations */}
          {corsHealth?.securityScore < 100 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Security Recommendations
              </h4>
              <ul className="space-y-1 text-sm text-blue-800">
                {corsHealth?.checks?.allowedOrigins?.status === 'error' && (
                  <li>• Add current origin to Supabase allowed origins list</li>
                )}
                {corsHealth?.checks?.allowedMethods?.status === 'warning' && (
                  <li>• Review and restrict HTTP methods to minimum required</li>
                )}
                {corsHealth?.checks?.credentials?.status === 'warning' && (
                  <li>• Enable credentials for authenticated API requests</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}